import { useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, TextField } from '@mui/material';
import type { DiscoveryAnswerProps } from './DiscoveryAnswerInput';
import { DISCOVERY_ADDED_CHOICES_LIMIT, DISCOVERY_CHOICE_LIMIT } from './discoveryLimits';

export const DiscoveryPickAny = ({ question, onAnswer }: DiscoveryAnswerProps) => {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const value = question.value ?? {};
  const ticked = Array.isArray(value.answer) ? value.answer : [];
  const custom = value.custom_choices ?? [];
  const options = [...(question.choices ?? []), ...custom];

  const toggle = (option: string, on: boolean) =>
    onAnswer({ ...value, answer: on ? [...ticked, option] : ticked.filter((chosen) => chosen !== option) });

  // An option typed the same as one already there ticks that one rather than adding a copy.
  const add = () => {
    const typed = draft.trim();
    if (!typed) return;
    const existing = options.find((option) => option.toLowerCase() === typed.toLowerCase());
    const option = existing ?? typed;
    onAnswer({
      ...value,
      custom_choices: existing ? custom : [...custom, typed],
      answer: ticked.includes(option) ? ticked : [...ticked, option]
    });
    setDraft('');
    setAdding(false);
  };

  return (
    <Box>
      <Box className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
        {options.map((option) => (
          <FormControlLabel
            key={option}
            label={option}
            control={
              <Checkbox
                size="small"
                checked={ticked.includes(option)}
                onChange={(event) => toggle(option, event.target.checked)}
              />
            }
            slotProps={{ typography: { fontSize: 14 } }}
          />
        ))}
      </Box>
      {adding ? (
        <Box className="flex items-center gap-2 mt-1">
          <TextField
            size="small"
            // Mounts only after "+ Add" is pressed, so the focus follows the user's own action.
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            value={draft}
            inputProps={{
              'aria-label': question.custom_choice_label ?? '+ Add option',
              maxLength: DISCOVERY_CHOICE_LIMIT
            }}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') add();
              if (event.key === 'Escape') setAdding(false);
            }}
          />
          <Button
            size="small"
            variant="contained"
            onClick={add}>
            Add
          </Button>
          <Button
            size="small"
            onClick={() => setAdding(false)}>
            Cancel
          </Button>
        </Box>
      ) : (
        custom.length < DISCOVERY_ADDED_CHOICES_LIMIT && (
          <Button
            size="small"
            variant="outlined"
            className="mt-1"
            onClick={() => setAdding(true)}>
            {question.custom_choice_label ?? '+ Add option'}
          </Button>
        )
      )}
    </Box>
  );
};
